import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  HiOutlineBriefcase,
  HiOutlineLocationMarker,
} from "react-icons/hi";
import {
  MdOutlineWorkOutline,
  MdOutlineCalendarToday,
  MdOutlineGroups,
} from "react-icons/md";
import { useLockBodyScroll } from "../../../Components/Hooks/useLockBodyScroll";

const CareerDetailModal = ({ career, onClose, onApply }) => {
  const [animate, setAnimate] = useState(false);
  useLockBodyScroll(!!career);

  useEffect(() => {
    if (career) setTimeout(() => setAnimate(true), 10);
  }, [career]);

  const handleClose = () => {
    setAnimate(false);
    setTimeout(onClose, 250);
  };

  const handleApplyClick = () => {
    setAnimate(false);
    setTimeout(() => {
      onClose();
      onApply(career);
    }, 250);
  };

  if (!career) return null;

  return createPortal(
    <div
      className={`fixed inset-0 z-50 flex items-end md:items-center justify-center
      bg-black/30 backdrop-blur-sm transition-opacity duration-300
      ${animate ? "opacity-100" : "opacity-0"}`}
      onClick={handleClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={`bg-white dark:bg-darkModeSecondaryBgColor w-full md:w-[560px] md:rounded-3xl rounded-t-3xl shadow-2xl flex flex-col transition-all duration-300 overflow-hidden
        ${animate ? "scale-100 translate-y-0" : "scale-95 md:translate-y-4 translate-y-full"}`}
        style={{ maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="px-5 py-4 bg-primaryColor text-white flex items-center rounded-t-3xl">
          <button onClick={handleClose} className="text-white text-2xl leading-none">
            ←
          </button>
          <div className="flex-1 text-center px-2">
            <p className="font-semibold text-lg truncate">{career.title}</p>
            <p className="text-[11px] text-white/80 truncate">
              {career.department || "Open Position"}
            </p>
          </div>
          <span className="w-6" />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-5 space-y-5">
          {/* Meta strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {career.location && (
              <div className="bg-slate-50 dark:bg-darkModeBgColor rounded-xl p-3 text-center">
                <HiOutlineLocationMarker className="mx-auto text-primaryColor mb-1" size={18} />
                <p className="text-[11px] font-semibold text-gray-600 dark:text-darkModeTextColor">
                  {career.location}
                </p>
              </div>
            )}
            <div className="bg-slate-50 dark:bg-darkModeBgColor rounded-xl p-3 text-center">
              <MdOutlineWorkOutline className="mx-auto text-primaryColor mb-1" size={18} />
              <p className="text-[11px] font-semibold text-gray-600 dark:text-darkModeTextColor">
                {career.jobType}
              </p>
            </div>
            {career.experienceLevel && (
              <div className="bg-slate-50 dark:bg-darkModeBgColor rounded-xl p-3 text-center">
                <MdOutlineGroups className="mx-auto text-primaryColor mb-1" size={18} />
                <p className="text-[11px] font-semibold text-gray-600 dark:text-darkModeTextColor">
                  {career.experienceLevel}
                </p>
              </div>
            )}
            {career.applicationDeadline && (
              <div className="bg-slate-50 dark:bg-darkModeBgColor rounded-xl p-3 text-center">
                <MdOutlineCalendarToday className="mx-auto text-primaryColor mb-1" size={18} />
                <p className="text-[11px] font-semibold text-gray-600 dark:text-darkModeTextColor">
                  {new Date(career.applicationDeadline).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                  })}
                </p>
              </div>
            )}
          </div>

          {career.salaryRange && (
            <div className="bg-primaryFadedColor dark:bg-darkModeBgColor rounded-xl p-3 text-center">
              <p className="text-xs font-semibold text-primaryColor">💰 {career.salaryRange}</p>
            </div>
          )}

          <div>
            <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-2">About the Role</h4>
            <p className="text-sm text-slate-600 dark:text-darkModeTextColor leading-relaxed whitespace-pre-line">
              {career.description}
            </p>
          </div>

          {career.responsibilities?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-2">Responsibilities</h4>
              <ul className="space-y-1.5">
                {career.responsibilities.map((item, i) => (
                  <li key={i} className="text-sm text-slate-600 dark:text-darkModeTextColor flex gap-2">
                    <span className="text-primaryColor mt-1">•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {career.requirements?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-2">Requirements</h4>
              <ul className="space-y-1.5">
                {career.requirements.map((item, i) => (
                  <li key={i} className="text-sm text-slate-600 dark:text-darkModeTextColor flex gap-2">
                    <span className="text-primaryColor mt-1">•</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {career.skills?.length > 0 && (
            <div>
              <h4 className="text-sm font-bold text-gray-800 dark:text-white mb-2">Skills</h4>
              <div className="flex flex-wrap gap-2">
                {career.skills.map((skill, i) => (
                  <span
                    key={i}
                    className="px-3 py-1 bg-slate-100 dark:bg-darkModeBgColor text-xs font-semibold text-gray-600 dark:text-darkModeTextColor rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 border-t dark:border-gray-700 flex gap-3">
          <button
            onClick={handleClose}
            className="flex-1 py-3.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-darkModeTextColor rounded-2xl font-medium"
          >
            Close
          </button>
          <button
            onClick={handleApplyClick}
            className="flex-1 py-3.5 bg-primaryColor text-white rounded-2xl font-semibold"
          >
            Apply Now
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CareerDetailModal;